import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { listarAplicacionesFertilizantes } from '../../services/aplicacionesFertilizantes';
import { useAuth } from '../../hooks/useAuth';
import { listPendingAplicacionesFertilizantesByUser } from '../../services/offline/offlineDbFertilizantes';
import { trySyncFertilizantes, isSyncingFertilizantes } from '../../services/offline/syncQueueFertilizantes';
import AppShell from '../../components/layout/AppShell';
import Button from '../../components/ui/Button';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';

const FILTROS = [
  { value: 'ACTIVA', label: 'Activas' },
  { value: 'ANULADA', label: 'Anuladas' },
  { value: 'TODAS', label: 'Todas' },
];

const SYNC_LABEL = {
  PENDIENTE: 'PENDIENTE DE SINCRONIZAR',
  SINCRONIZANDO: 'SINCRONIZANDO',
  ERROR: 'ERROR AL SINCRONIZAR',
};

function formatFecha(fechaaplicacion) {
  if (!fechaaplicacion) return '—';
  try {
    return new Date(`${fechaaplicacion}T00:00:00`).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return fechaaplicacion;
  }
}

function formatHoraRango(horainicio, horafin) {
  const inicio = horainicio ? horainicio.slice(0, 5) : null;
  const fin = horafin ? horafin.slice(0, 5) : null;
  if (!inicio && !fin) return '—';
  return `${inicio || '—'} – ${fin || '—'}`;
}

function formatHa(ha) {
  if (ha === null || ha === undefined || ha === '') return '—';
  return `${Number(ha).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ha`;
}

// Un registro pendiente local no tiene id de servidor todavía: se adapta al
// mismo shape que usan las tarjetas/filas para poder mezclarlos en un solo
// listado, marcado con isLocal (mismo patrón que AplicacionesPage).
function pendienteToRow(item) {
  return {
    id: `local-${item.client_operation_id}`,
    isLocal: true,
    clientOperationId: item.client_operation_id,
    fechaaplicacion: item.payload?.fechaaplicacion,
    horainicio: item.payload?.horainicio,
    horafin: item.payload?.horafin,
    huerto: item.payload?.huerto,
    cuartel: item.payload?.cuartel,
    superficie_ha: item.payload?.superficie_ha,
    cantidadProductos: Array.isArray(item.payload?.productos) ? item.payload.productos.length : 0,
    syncLabel: SYNC_LABEL[item.estado_local] || SYNC_LABEL.PENDIENTE,
    errorMessage: item.error_message,
  };
}

function FertilizantesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const justSavedOffline = Boolean(location.state?.justSavedOffline);

  const [aplicaciones, setAplicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('ACTIVA');

  const [pendientes, setPendientes] = useState([]);
  const [syncingNow, setSyncingNow] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    listarAplicacionesFertilizantes().then((result) => {
      if (result.ok) {
        setAplicaciones(result.aplicaciones);
      } else {
        setError(result.message);
      }
      setLoading(false);
    });
  }, []);

  const cargarPendientes = useCallback(() => {
    if (!user?.id) return;
    listPendingAplicacionesFertilizantesByUser(user.id).then(setPendientes);
  }, [user?.id]);

  useEffect(() => {
    cargar();
    cargarPendientes();
  }, [cargar, cargarPendientes]);

  useEffect(() => {
    function handleOfflineChange() {
      cargarPendientes();
      setSyncingNow(isSyncingFertilizantes());
      cargar();
    }
    window.addEventListener('fg-offline-fertilizantes-change', handleOfflineChange);
    return () => window.removeEventListener('fg-offline-fertilizantes-change', handleOfflineChange);
  }, [cargar, cargarPendientes]);

  const handleSincronizarClick = async () => {
    if (!user?.id) return;
    setSyncingNow(true);
    await trySyncFertilizantes(user.id);
    setSyncingNow(false);
  };

  const filtradasServidor = useMemo(
    () => aplicaciones.filter((a) => (filtro === 'TODAS' ? true : a.estado === filtro)),
    [aplicaciones, filtro]
  );

  const pendientesVisibles = filtro === 'ANULADA' ? [] : pendientes.map(pendienteToRow);
  const filtradas = [...pendientesVisibles, ...filtradasServidor];

  return (
    <AppShell title="Fertilizantes">
      <div className="flex flex-col gap-6">
        {justSavedOffline && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-center text-sm font-medium text-amber-700">
            Sin conexión: la aplicación quedó guardada en tu teléfono y se sincronizará automáticamente.
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-bold text-gray-900">Aplicaciones de fertilizantes</h1>
          <Button className="sm:w-auto sm:px-6" onClick={() => navigate('/fertilizantes/nueva')}>
            <Icon name="plus" className="mr-1.5 h-5 w-5" />
            Nueva aplicación
          </Button>
        </div>

        {pendientes.length > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-800">
              {pendientes.length} pendiente{pendientes.length === 1 ? '' : 's'} de sincronizar
            </p>
            <button
              type="button"
              onClick={handleSincronizarClick}
              disabled={syncingNow}
              className="shrink-0 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-amber-800 shadow-sm transition-colors hover:bg-amber-100 disabled:opacity-60"
            >
              {syncingNow ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          </div>
        )}

        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 whitespace-nowrap">
            {FILTROS.map((filtroOpcion) => (
              <button
                key={filtroOpcion.value}
                type="button"
                onClick={() => setFiltro(filtroOpcion.value)}
                className={`min-h-[40px] shrink-0 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ${
                  filtro === filtroOpcion.value
                    ? 'border border-green-200 bg-green-50 text-green-700'
                    : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {filtroOpcion.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-700">{error}</p>}

        {loading ? (
          <p className="text-center text-sm text-gray-500">Cargando...</p>
        ) : filtradas.length === 0 ? (
          <EmptyState message="No hay aplicaciones de fertilizantes en este filtro." />
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-2xl border border-gray-100 bg-white lg:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Fecha</th>
                    <th className="px-4 py-3 font-medium">Hora</th>
                    <th className="px-4 py-3 font-medium">Ubicación</th>
                    <th className="px-4 py-3 font-medium">Sup.</th>
                    <th className="px-4 py-3 font-medium">Productos</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtradas.map((aplicacion) => (
                    <tr
                      key={aplicacion.id}
                      onClick={() => !aplicacion.isLocal && navigate(`/fertilizantes/${aplicacion.id}`)}
                      className={`transition-colors ${
                        aplicacion.isLocal ? '' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 text-gray-900">{formatFecha(aplicacion.fechaaplicacion)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatHoraRango(aplicacion.horainicio, aplicacion.horafin)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {aplicacion.huerto || '—'} · {aplicacion.cuartel || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatHa(aplicacion.superficie_ha)}</td>
                      <td className="px-4 py-3 text-gray-600">{aplicacion.cantidadProductos}</td>
                      <td className="px-4 py-3">
                        <StatusBadge estado={aplicacion.isLocal ? aplicacion.syncLabel : aplicacion.estado} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 lg:hidden">
              {filtradas.map((aplicacion) => (
                <div
                  key={aplicacion.id}
                  onClick={() => !aplicacion.isLocal && navigate(`/fertilizantes/${aplicacion.id}`)}
                  role={aplicacion.isLocal ? undefined : 'button'}
                  tabIndex={aplicacion.isLocal ? undefined : 0}
                  onKeyDown={(event) => {
                    if (!aplicacion.isLocal && event.key === 'Enter') navigate(`/fertilizantes/${aplicacion.id}`);
                  }}
                  className={`rounded-2xl border bg-white p-4 shadow-sm transition-colors ${
                    aplicacion.isLocal
                      ? 'border-amber-200'
                      : 'cursor-pointer border-gray-100 hover:border-green-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-500">
                      {formatFecha(aplicacion.fechaaplicacion)} ·{' '}
                      {formatHoraRango(aplicacion.horainicio, aplicacion.horafin)}
                    </p>
                    <StatusBadge estado={aplicacion.isLocal ? aplicacion.syncLabel : aplicacion.estado} />
                  </div>

                  <p className="mt-2 font-semibold text-gray-900">{aplicacion.huerto || '—'}</p>
                  <p className="text-sm text-gray-500">{aplicacion.cuartel || '—'}</p>

                  <p className="mt-2 text-xs text-gray-400">
                    {formatHa(aplicacion.superficie_ha)} · {aplicacion.cantidadProductos} producto
                    {aplicacion.cantidadProductos === 1 ? '' : 's'}
                  </p>

                  {aplicacion.isLocal && aplicacion.errorMessage && (
                    <p className="mt-2 text-xs text-red-600">{aplicacion.errorMessage}</p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

export default FertilizantesPage;
